import { useRef, useState } from 'react';
import type { ResultadoEnvioCampana } from '../../types/fondosCampana';
import { esResultadoCampanaIncierto } from '../../utils/fondosCampanaRpc';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';

type Resultado = ResultadoEnvioCampana;
type Respuesta = { readonly estado: 'confirmado'; readonly resultado: Resultado }
  | { readonly estado: 'fallido' };

function codigoError(error: unknown): string | null {
  return typeof error === 'object' && error !== null && 'code' in error
    && typeof error.code === 'string' ? error.code : null;
}

export function useOperacionEnvio() {
  const intento = useRef<(() => Promise<Resultado>) | null>(null);
  const enCurso = useRef(false);
  const concluido = useRef(false);
  const [procesando, setProcesando] = useState(false);
  const [incierto, setIncierto] = useState(false);
  const [completado, setCompletado] = useState(false);
  const [revisionObsoleta, setRevisionObsoleta] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ejecutar = async (crear: (solicitudId: string) => () => Promise<Resultado>): Promise<Respuesta> => {
    if (enCurso.current || concluido.current) return { estado: 'fallido' };
    intento.current ??= crear(crypto.randomUUID());
    enCurso.current = true;
    setProcesando(true);
    setError(null);
    try {
      const resultado = await intento.current();
      concluido.current = true;
      setCompletado(true);
      setIncierto(false);
      intento.current = null;
      return { estado: 'confirmado', resultado };
    } catch (e: unknown) {
      if (esResultadoCampanaIncierto(e)) {
        setIncierto(true);
        setError('El resultado está pendiente de verificar. Reintenta la misma operación; no realices otra transferencia.');
      } else {
        intento.current = null;
        setIncierto(false);
        setRevisionObsoleta(codigoError(e) === '40001');
        setError(mensajeError(e));
      }
      reportarErrorInesperado(e, 'No pudimos completar la operación del envío.');
      return { estado: 'fallido' };
    } finally {
      enCurso.current = false;
      setProcesando(false);
    }
  };

  const revisionRecargada = () => { setRevisionObsoleta(false); setError(null); };
  return { ejecutar, procesando, incierto, completado, revisionObsoleta, revisionRecargada, error };
}

function mensajeError(error: unknown): string {
  switch (codigoError(error)) {
    case '40001': return 'La rendición cambió. Recárgala y revisa sus datos antes de continuar.';
    case '42501': return 'Esta operación corresponde a la junta responsable de esa entidad.';
    case '23505': return 'El envío requiere conciliar su registro antes de continuar.';
    default: return 'Revisa el importe, saldo disponible, fecha y situación del envío. Regulariza primero las operaciones dependientes.';
  }
}
